import {Text} from '@/components-v2/atoms/Text/Text'
import {Dropdown} from '@/components-v2/molecules/Dropdown/Dropdown'
import {FileUpload} from '@/components-v2/molecules/FileUpload/FileUpload'
import {Modal} from '@/components-v2/organisms/Modal/Modal'
import {useUploadMediaModal} from './useUploadMediaModal'

// entityTypeOptions is `{id, displayText}[]` (Dropdown's own option shape) - put the option that
// should be selected by default first. A single option skips the dropdown entirely.
export const UploadMediaModal = ({openUpload, setOpenUpload, title = 'Upload Media', entityTypeOptions}) => {
  const showEntityTypeDropdown = entityTypeOptions.length > 1
  const defaultEntityType = entityTypeOptions[0]?.id

  const {
    entityType,
    setEntityType,
    files,
    hasErrors,
    uploadErrors,
    uploading,
    imageUri,
    maxImageCollectionSize,
    maxAllowedImageCount,
    maxIndividualImageSize,
    handleFileUpdate,
    handleFileDelete,
    handleFileError,
    handleUpload,
    isCloseModal,
  } = useUploadMediaModal({setOpenUpload, initialEntityType: defaultEntityType})

  const selectedEntityTypeValue = [
    entityTypeOptions.find(option => option.id === entityType) ?? {id: entityType, displayText: entityType},
  ]

  const handleEntityTypeChange = value => {
    const selected = Array.isArray(value) && value.length > 0 ? value[0].id : defaultEntityType
    setEntityType(selected)
  }

  return (
    <Modal
      showModal={openUpload}
      onCloseModal={isCloseModal}
      title={title}
      type="S"
      showSecondaryButton
      showTertiaryButton={false}
      secondaryButtonProps={{text: 'Cancel', variant: 'secondary', onClick: isCloseModal, disabled: uploading}}
      primaryButtonProps={{
        text: uploading ? 'Uploading...' : 'Done',
        onClick: handleUpload,
        isLoading: uploading,
        disabled: uploading || hasErrors || files.length <= 0 || files.length > maxAllowedImageCount,
      }}
      data-cy="upload-images-modal"
    >
      <div className="flex min-h-[340px] flex-col gap-4">
        <Text size="small">
          {`Upload up to ${maxAllowedImageCount} images (${maxIndividualImageSize} per image, ${maxImageCollectionSize} total). Each file name must match its selected entity name to link successfully.`}
        </Text>
        {showEntityTypeDropdown && (
          <Dropdown
            label="Type of Image*"
            childList={entityTypeOptions}
            selection="single"
            isClearable={false}
            placeholder="Select image type"
            selectedValue={selectedEntityTypeValue}
            setSelectedValue={handleEntityTypeChange}
            disabled={files.length > 0}
          />
        )}
        <FileUpload
          description="Drag and drop your file here (only .jpg, .png, .webp)"
          accept=".jpg,.png,.webp"
          buttonLabel="Upload File"
          // FileUploadV2 only shows the multi-file list for type="default" - "image" is a
          // single-file preview, so switch based on how many files are selected (and fall back
          // to the list, which supports per-row error highlighting, if that one file errored).
          type={files.length === 1 && !hasErrors ? 'image' : 'default'}
          multiple
          files={files}
          maxFiles={maxAllowedImageCount}
          imageUri={imageUri}
          variant={uploading ? 'uploading' : 'edit'}
          feedbackType={hasErrors ? 'error' : 'default'}
          helperText={hasErrors ? uploadErrors.join(', ') : undefined}
          onUpdate={handleFileUpdate}
          onDelete={handleFileDelete}
          onFileError={handleFileError}
          data-cy="upload-images"
          uploadIcon="image"
        />
      </div>
    </Modal>
  )
}
